import { v4 as uuidv4 } from 'uuid';

import { Telemetry } from '../../commonInfra/services/Telemetry';
import { SearchEPTEvent, SearchEventSource, SearchFiltersUpdatedEvent, SearchImpressionEvent, SearchInitiatedEvent, SearchNoResultEvent, SearchResultClickedEvent, SearchTermUpdatedEvent, TelemetryEvents } from '../../commonInfra/telemetryEvents/TelemetryEvents';

interface ISearchTelemetry {
  clearSearchFlowId: () => void;
  startSearchFlow: (eventSource: SearchEventSource) => void;
  isSearchFlowStarted: () => boolean;
  sendSearchTermUpdatedEvent: (eventSource: SearchEventSource, searchTerm: string) => void;
  sendSearchEPTEvent: (sessionType: string,
    searchTerm: string,
    durationFromStartToWhenInitialUIRendered: number,
    durationFromWhenInitialUIRenderedToWhenSendingRequestsToServerSide: number,
    durationFromWhenSendingRequestsToServerSideToWhenGotResponseFromServerSide: number,
    durationFromWhenGotResponseFromServerSideToRenderFullData: number,
    totalDuration: number,
    clientRequestID?: string) => void;
  endSearchFlowWithNoResultEvent: (eventSource: SearchEventSource, searchTerm: string) => void;
  sendSearchFiltersUpdatedEvent: (event: SearchFiltersUpdatedEvent) => void;
  sendSearchResultClickedEvent: (event: Omit<SearchResultClickedEvent, 'searchFlowId'>, skipClearSearchFlowId?: boolean) => void;
  sendSearchImpressionEvent: (searchTerm: string, impressionType: string) => void;
  ensureInitialization: (isOnlineEnvironemnt: boolean) => void;
}

class SearchTelemetry implements ISearchTelemetry {
  private isOnlineEnvironemnt = false;
  private searchFlowId: string = '';

  public ensureInitialization = (isOnlineEnvironemnt: boolean) => {
    this.isOnlineEnvironemnt = isOnlineEnvironemnt;
  };

  public startSearchFlow = (eventSource: SearchEventSource) => {
    this.searchFlowId = uuidv4();

    Telemetry.sendEvent<SearchInitiatedEvent>({
      type: TelemetryEvents.SearchInitiated,
      eventSource,
      searchFlowId: this.searchFlowId
    });
  };

  public clearSearchFlowId = () => {
    this.searchFlowId = '';
  };

  public isSearchFlowStarted = () => {
    return this.searchFlowId !== '';
  };

  public sendSearchTermUpdatedEvent = (eventSource: SearchEventSource, searchTerm: string) => {
    if (!this.isOnlineEnvironemnt) {
      searchTerm = null;
    }

    Telemetry.sendEvent<SearchTermUpdatedEvent>({
      type: TelemetryEvents.SearchTermUpdated,
      eventSource,
      searchTerm,
      searchFlowId: this.searchFlowId
    });
  };

  public sendSearchEPTEvent = (eptType: string,
    searchTerm: string,
    durationFromStartToWhenInitialUIRendered: number,
    durationFromWhenInitialUIRenderedToWhenSendingRequestsToServerSide: number,
    durationFromWhenSendingRequestsToServerSideToWhenGotResponseFromServerSide: number,
    durationFromWhenGotResponseFromServerSideToRenderFullData: number,
    totalDuration: number,
    clientRequestID?: string) => {
    if (!this.isOnlineEnvironemnt) {
      searchTerm = null;
    }

    Telemetry.sendEvent<SearchEPTEvent>({
      type: TelemetryEvents.SearchEPT,
      ept_type: eptType,
      search_term: searchTerm,
      search_flow_id: this.searchFlowId,
      duration_from_start_to_phase_a: durationFromStartToWhenInitialUIRendered,
      duration_from_phase_a_to_phase_b: durationFromWhenInitialUIRenderedToWhenSendingRequestsToServerSide,
      duration_from_phase_b_to_phase_c: durationFromWhenSendingRequestsToServerSideToWhenGotResponseFromServerSide,
      duration_from_phase_c_to_end: durationFromWhenGotResponseFromServerSideToRenderFullData,
      total_duration: totalDuration,
      client_request_id: clientRequestID
    });
  };

  public endSearchFlowWithNoResultEvent = (eventSource: SearchEventSource, searchTerm: string) => {
    if (!this.isOnlineEnvironemnt) {
      searchTerm = null;
    }

    if (this.searchFlowId === '') { return; }
    Telemetry.sendEvent<SearchNoResultEvent>({
      type: TelemetryEvents.SearchNoResult,
      eventSource,
      searchTerm,
      searchFlowId: this.searchFlowId
    });
    this.clearSearchFlowId();
  };

  public sendSearchImpressionEvent = (searchTerm: string, impressionType: string) => {
    Telemetry.sendEvent<SearchImpressionEvent>({
      type: TelemetryEvents.SearchImpression,
      search_flow_id: this.searchFlowId,
      searchTerm,
      impression_type: impressionType
    });
  };

  public sendSearchResultClickedEvent = (event: Omit<SearchResultClickedEvent, 'searchFlowId'>, skipClearSearchFlowId?: boolean) => {
    if (!this.isOnlineEnvironemnt) {
      event.searchTerm = null;
    }
    Telemetry.sendEvent<SearchResultClickedEvent>({ ...event as SearchResultClickedEvent, searchFlowId: this.searchFlowId });
    if (!(event.seeAll || skipClearSearchFlowId)) {
      this.clearSearchFlowId();
    }
  };

  public sendSearchFiltersUpdatedEvent = (event: SearchFiltersUpdatedEvent) => {
    if (!this.isOnlineEnvironemnt) {
      event.searchTerm = null;
    }

    Telemetry.sendEvent<SearchFiltersUpdatedEvent>({ ...event as SearchFiltersUpdatedEvent, search_flow_id: this.searchFlowId });
  };
}

export const searchTelemetry: ISearchTelemetry = new SearchTelemetry();
